// Display vocabulary and formatters shared across screens: statuses, stages,
// the test taxonomy, and how money, counts and dates render. Values here must
// match the enums in prisma/schema.prisma; the server validates against those,
// this file only labels them.

// HTML collapses runs of whitespace, so joined metadata needs a real character
// between the parts or it reads as one run-on string.
export const SEPARATOR = ' · '

// A list of { value, label, ... } plus a label lookup by value. How a value
// reads as a Pill tone lives in client/src/tones/, one file per domain.
function vocabulary(entries) {
  const byValue = Object.fromEntries(entries.map((entry) => [entry.value, entry]))
  return {
    entries,
    label: (value) => byValue[value]?.label ?? value,
  }
}

// An account we serve.
const customerStatuses = vocabulary([
  { value: 'ACTIVE', label: 'Active' },
  { value: 'DORMANT', label: 'Dormant' },
  { value: 'LOST', label: 'Lost' },
])

export const CUSTOMER_STATUSES = customerStatuses.entries
export const statusLabel = customerStatuses.label

const onsiteStatuses = vocabulary([
  { value: 'REQUESTED', label: 'Requested' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELED', label: 'Canceled' },
])

export const onsiteStatusLabel = onsiteStatuses.label

// The monthly activity import, from upload to committed records.
const importStatuses = vocabulary([
  { value: 'DRAFT', label: 'Parsing' },
  { value: 'READY', label: 'Ready to review' },
  { value: 'COMMITTED', label: 'Committed' },
])

export const importStatusLabel = importStatuses.label
export {
  activitySourceLabel,
  ACTIVITY_SOURCES,
  ACTIVITY_SOURCE,
} from '../../shared/activity-sources.js'

const issueSeverities = vocabulary([
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'CRITICAL', label: 'Critical' },
])

export const ISSUE_SEVERITIES = issueSeverities.entries
export const severityLabel = issueSeverities.label

const testCategories = vocabulary([
  { value: 'DOT_URINE', label: 'DOT urine' },
  { value: 'NON_DOT_RAPID_URINE', label: 'Rapid urine' },
  { value: 'NON_DOT_LAB_URINE', label: 'Lab urine' },
  { value: 'COLLECT_ONLY', label: 'Collect only' },
  { value: 'BREATH_ALCOHOL', label: 'Breath alcohol' },
  { value: 'ORAL_FLUID', label: 'Oral fluid' },
  { value: 'HAIR', label: 'Hair' },
  { value: 'NAIL', label: 'Nail' },
  { value: 'DNA', label: 'DNA' },
  { value: 'DOT_PHYSICAL', label: 'DOT physical' },
  { value: 'DOT_CONSORTIUM', label: 'DOT consortium' },
  { value: 'ONSITE', label: 'Onsite' },
])

export const TEST_CATEGORIES = testCategories.entries
export const categoryLabel = testCategories.label

const specimenTypes = vocabulary([
  { value: 'URINE', label: 'Urine' },
  { value: 'BREATH', label: 'Breath' },
  { value: 'ORAL_FLUID', label: 'Oral fluid' },
  { value: 'HAIR', label: 'Hair' },
  { value: 'NAIL', label: 'Nail' },
  { value: 'BUCCAL', label: 'Buccal' },
  { value: 'NONE', label: 'None' },
  { value: 'OTHER', label: 'Other' },
])

export const SPECIMEN_TYPES = specimenTypes.entries
export const specimenLabel = specimenTypes.label

// Money arrives as a string so it never passes through a float. Format for
// display only, and never round trip the formatted value back to the server.
function currency(value, fractionDigits) {
  if (value === null || value === undefined || value === '') return null
  return Number(value).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })
}

// Rollup totals round to whole dollars. Unit prices keep their cents, because
// 26.50 shown as 27 dollars would be wrong on its face.
export const formatMoney = (value) => currency(value, 0)
export const formatPrice = (value) => currency(value, 2)

export function formatCount(value) {
  if (value === null || value === undefined) return null
  return Number(value).toLocaleString()
}

// A service month travels as its first day, matching the database convention.
// The label is the human name for the whole month.
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export function monthLabel(value) {
  const [year, month] = value.split('-').map(Number)
  return `${MONTH_NAMES[month - 1]} ${year}`
}

export function currentMonth() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
}

export function shiftMonth(value, step) {
  const [year, month] = value.split('-').map(Number)
  const date = new Date(year, month - 1 + step, 1)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`
}

export function todayDateOnly() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`
}

// A timestamp shown to a person. The year is on by default; tight table
// columns like the onsite queue turn it off.
export function formatInstant(value, { year = true } = {}) {
  if (!value) return null
  return new Date(value).toLocaleString(undefined, {
    ...(year ? { year: 'numeric' } : {}),
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

// A stored instant split back into the date and clock time the intake forms
// edit separately, because a raw datetime string is easy to get wrong on the
// phone.
export function fromInstant(value) {
  if (!value) return { date: '', time: '' }
  const instant = new Date(value)
  const pad = (part) => String(part).padStart(2, '0')
  return {
    date: `${instant.getFullYear()}-${pad(instant.getMonth() + 1)}-${pad(instant.getDate())}`,
    time: `${pad(instant.getHours())}:${pad(instant.getMinutes())}`,
  }
}

// A date column is a calendar day, so parse it as one rather than as an
// instant, which would shift it by a day west of UTC.
function parseDateOnly(value) {
  if (!value) return null
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year, month - 1, day)
}

export function formatDate(value) {
  const date = parseDateOnly(value)
  if (!date) return null
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export { formatBytes } from './components/ui/formatBytes.js'

// Whole days from a calendar day to today, or null when unset. Feeds the
// directory's 30-day recency coloring.
export function daysSince(value) {
  const date = parseDateOnly(value)
  if (!date) return null
  const today = new Date()
  const start = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
  const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
  return Math.floor((now - start) / 86400000)
}

// Elapsed time since the relationship started. Stored as a date, shown as
// tenure, because how long they have been with us is the thing anyone reading
// the directory actually wants.
export function formatTenure(value) {
  const start = parseDateOnly(value)
  if (!start) return null

  const now = new Date()
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth())
  if (now.getDate() < start.getDate()) months -= 1
  if (months < 0) return 'Starts soon'
  if (months < 1) return 'Under a month'
  if (months < 12) return `${months} ${months === 1 ? 'month' : 'months'}`

  const years = Math.floor(months / 12)
  const remainder = months % 12
  const yearPart = `${years} ${years === 1 ? 'year' : 'years'}`
  return remainder ? `${yearPart}, ${remainder} ${remainder === 1 ? 'month' : 'months'}` : yearPart
}
